import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Keeps the Home page Workflow cards' RESULTS alive across page navigation
 * AND a full page reload.
 *
 * MenuDataExtraction and ImageCreation normally live only inside MerchantHome,
 * which React Router fully unmounts whenever the merchant navigates to another
 * page (e.g. /modes, /schedule) — any plain useState in those cards would be
 * wiped and have to be rebuilt from scratch on return. This provider is
 * mounted once above <Routes> in App.js, so it survives route changes for the
 * life of the app session.
 *
 * On top of that, this also mirrors to sessionStorage: some pages (the AI
 * Menu screen, when a store's menu hasn't been saved yet) do a real
 * window.location.reload(), which wipes plain in-memory state completely.
 * sessionStorage survives that reload — it's only cleared when the browser
 * tab/window is closed — so the extracted JSON / generated images are still
 * there when the merchant comes back, without needing a database change.
 *
 * Scope is intentionally narrow: just the extracted menu JSON and the
 * generated image versions — the actual results the merchant comes back to
 * view — not every in-progress form field (draft title/price/prompt text
 * etc. stays local to each card, as before).
 */
const WorkflowStateContext = createContext(null);

const STORAGE_KEY = "ezwinds_workflow_state_v1";
// Generated images are base64 data URLs and can be a few hundred KB to a few
// MB each — sessionStorage is quota-limited (~5-10MB per origin), so only the
// most recent versions are persisted. The full, uncapped list still lives in
// memory for the rest of the current page session; this cap only affects what
// comes back after a reload.
const MAX_PERSISTED_VERSIONS = 5;

const readPersisted = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null; // corrupted or inaccessible storage — start fresh, never throw
  }
};

export function WorkflowStateProvider({ children }) {
  // Menu Data Extraction
  const [extractedSections, setExtractedSections] = useState(() => readPersisted()?.extractedSections || []);
  // The items ticked for hand-off to Image Extraction (several can be sent together). An older saved
  // session stored a single key; it is read back as a one-item list.
  const [extractionSelectedKeys, setExtractionSelectedKeys] = useState(() => {
    const saved = readPersisted();
    if (Array.isArray(saved?.extractionSelectedKeys)) return saved.extractionSelectedKeys;
    return saved?.extractionSelectedKey != null ? [saved.extractionSelectedKey] : [];
  });

  // Image Creation
  const [imageVersions, setImageVersions] = useState(() => readPersisted()?.imageVersions || []);
  const [imageSelectedKey, setImageSelectedKey] = useState(() => readPersisted()?.imageSelectedKey || "");

  // Mirror every change to sessionStorage so a reload (in-app or a full
  // browser refresh) can restore it. Never lets a storage error (quota, a
  // browser blocking storage in private mode, etc.) break the app — the
  // in-memory state above keeps working either way.
  useEffect(() => {
    try {
      const cappedVersions = imageVersions.slice(0, MAX_PERSISTED_VERSIONS);
      // If the capped list drops the currently selected version, fall back to
      // the newest one that's still there rather than persisting a selectedKey
      // that won't match anything after a reload.
      const cappedSelectedKey = cappedVersions.some((v) => v.key === imageSelectedKey)
        ? imageSelectedKey
        : cappedVersions[0]?.key || "";
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          extractedSections,
          extractionSelectedKeys,
          imageVersions: cappedVersions,
          imageSelectedKey: cappedSelectedKey,
        })
      );
    } catch (err) {
      console.warn("Workflow state: couldn't save to sessionStorage (continuing in memory only):", err?.message);
    }
  }, [extractedSections, extractionSelectedKeys, imageVersions, imageSelectedKey]);

  const value = {
    extractedSections,
    setExtractedSections,
    extractionSelectedKeys,
    setExtractionSelectedKeys,
    imageVersions,
    setImageVersions,
    imageSelectedKey,
    setImageSelectedKey,
  };

  return <WorkflowStateContext.Provider value={value}>{children}</WorkflowStateContext.Provider>;
}

export function useWorkflowState() {
  const ctx = useContext(WorkflowStateContext);
  if (!ctx) {
    throw new Error("useWorkflowState must be used within a WorkflowStateProvider");
  }
  return ctx;
}
